import type { AutomationTarget, Project, Track, WamParameterInfo } from '@/domain/types';
import type { TransportTell } from './wamTransport';
import { MAX_COMPENSATION_SEC } from './latency';

/** What a hosted plugin offers the graph beyond audio: its state and the transport. */
export interface HostedPlugin {
  captureState?(): Promise<unknown>;
  tellTransport?(tells: readonly TransportTell[]): void;
}
import { isTrackAudible } from '@/domain/selectors';
import { createEffectNode, type AutomatableParam, type EffectNode } from './effects/EffectNode';
import { createInstrumentNode, type BufferResolver, type InstrumentNode } from './instruments';

/**
 * The per-track audio graph.
 *
 * One `TrackGraph` owns every node for one channel strip and knows how to
 * reconcile itself against a `Track` without tearing the graph down. That
 * matters: rebuilding a graph mid-playback drops the audio flowing through it,
 * so a volume change must never cause a rebuild. Only a change to the *shape*
 * of the chain (an effect added, removed or reordered) re-patches anything, and
 * even then only the insert chain.
 *
 * Signal path:
 *
 *   (instrument) -> input -> [effect 1 .. effect n] -> compensation -> preFaderTap
 *                -> fader -> muteGain -> panner -> postFaderTap -> outputCompensation
 *                -> meter -> output -> destination
 *
 * Sends branch off `preFaderTap` (pre) or `postFaderTap` (post), each through
 * its own gain and its own compensation delay — before the output's own
 * compensation, which is the output route's alone.
 *
 * The fader and the mute are separate nodes on purpose. Automation writes
 * scheduled ramps onto the fader's gain, and a mute has to be able to cut the
 * channel *without* fighting those scheduled values — writing both to one param
 * means whichever wrote last wins, and the channel un-mutes itself the moment
 * the next automation event lands.
 */

export interface MeterReading {
  peakLeft: number;
  peakRight: number;
  rmsLeft: number;
  rmsRight: number;
  clipped: boolean;
}

const SILENT_METER: MeterReading = { peakLeft: 0, peakRight: 0, rmsLeft: 0, rmsRight: 0, clipped: false };

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

export class TrackGraph {
  readonly input: GainNode;
  private readonly preFaderTap: GainNode;
  private readonly fader: GainNode;
  private readonly muteGain: GainNode;
  private readonly panner: StereoPannerNode | null;
  private readonly panFallback: GainNode | null;
  private readonly splitter: ChannelSplitterNode;
  private readonly analyserLeft: AnalyserNode;
  private readonly analyserRight: AnalyserNode;
  readonly output: GainNode;
  private readonly compensation: DelayNode;
  private readonly outputCompensation: DelayNode;
  private readonly postFaderTap: GainNode;

  private effects: { id: string; node: EffectNode }[] = [];
  /** Effects still wiring themselves up; each drops out as it settles. */
  private loading = new Set<Promise<void>>();
  private effectSignature = '';
  private instrument: InstrumentNode | null = null;
  private instrumentIdentity: string | null = null;
  private sendNodes = new Map<string, { gain: GainNode; delay: DelayNode }>();
  private readonly bufferLeft: Float32Array<ArrayBuffer>;
  private readonly bufferRight: Float32Array<ArrayBuffer>;
  private clipHoldUntil = 0;

  constructor(
    private readonly ctx: BaseAudioContext,
    readonly trackId: string,
    /** How the track's instrument reaches decoded audio; samplers need it. */
    private readonly resolveBuffer: BufferResolver = () => null,
    /** Told what a hosted plugin said about itself once it loaded, so the project can remember; `null` names the instrument. */
    private readonly onWamInfo: ((effectId: string | null, info: { name: string; parameters: WamParameterInfo[] }) => void) | null = null,
  ) {
    this.input = ctx.createGain();
    this.preFaderTap = ctx.createGain();
    this.fader = ctx.createGain();
    this.muteGain = ctx.createGain();
    this.output = ctx.createGain();

    // StereoPannerNode is universally available in modern browsers; the gain
    // fallback keeps the graph valid on anything that lacks it rather than
    // failing to produce sound at all.
    if (typeof ctx.createStereoPanner === 'function') {
      this.panner = ctx.createStereoPanner();
      this.panFallback = null;
    } else {
      this.panner = null;
      this.panFallback = ctx.createGain();
    }

    this.splitter = ctx.createChannelSplitter(2);
    this.analyserLeft = ctx.createAnalyser();
    this.analyserRight = ctx.createAnalyser();
    this.analyserLeft.fftSize = 1024;
    this.analyserRight.fftSize = 1024;
    this.analyserLeft.smoothingTimeConstant = 0;
    this.analyserRight.smoothingTimeConstant = 0;
    this.bufferLeft = new Float32Array(new ArrayBuffer(this.analyserLeft.fftSize * 4));
    this.bufferRight = new Float32Array(new ArrayBuffer(this.analyserRight.fftSize * 4));

    const panNode: AudioNode = this.panner ?? this.panFallback!;
    // Plugin delay compensation sits at the end of the insert chain, before
    // the pre-fader tap: a leaf track whose path to the master is shorter
    // than the longest is held back here by the difference, so everything
    // arrives at the master together — and a pre-fader send, which branches
    // off just after, is held back by the same amount as the output.
    this.compensation = ctx.createDelay(MAX_COMPENSATION_SEC);
    this.compensation.delayTime.value = 0;
    this.compensation.connect(this.preFaderTap);
    // What the output route needs beyond what every route shares.
    this.outputCompensation = ctx.createDelay(MAX_COMPENSATION_SEC);
    this.outputCompensation.delayTime.value = 0;
    this.postFaderTap = ctx.createGain();
    this.preFaderTap
      .connect(this.fader)
      .connect(this.muteGain)
      .connect(panNode)
      .connect(this.postFaderTap)
      .connect(this.outputCompensation)
      .connect(this.output);

    // Metering taps the post-pan signal without being in the audible path.
    this.output.connect(this.splitter);
    this.splitter.connect(this.analyserLeft, 0);
    this.splitter.connect(this.analyserRight, 1);

    this.rebuildEffectChain([]);
  }

  /**
   * Applies every property of a track that does not change the graph shape.
   *
   * `automatedParams` names the parameters an automation lane currently owns.
   * Those are left alone: a static write would fight the scheduled ramps and
   * produce a parameter that jumps whenever the UI re-renders.
   */
  update(project: Project, track: Track, automatedParams: ReadonlySet<string> = EMPTY_SET): void {
    const audible = isTrackAudible(project, track.id);
    // A short ramp instead of a step: instantaneous gain changes click.
    this.muteGain.gain.setTargetAtTime(audible ? 1 : 0, this.ctx.currentTime, 0.015);
    if (!automatedParams.has('volume')) {
      this.fader.gain.setTargetAtTime(track.volume, this.ctx.currentTime, 0.015);
    }
    if (this.panner && !automatedParams.has('pan')) {
      this.panner.pan.setTargetAtTime(track.pan, this.ctx.currentTime, 0.015);
    }

    this.updateInstrument(track, automatedParams);

    const signature = track.effects.map((e) => `${e.id}:${e.type}`).join('|');
    const rebuilt = signature !== this.effectSignature;
    if (rebuilt) this.rebuildEffectChain(track.effects);
    for (const effect of track.effects) {
      const node = this.effects.find((e) => e.id === effect.id)?.node;
      if (!node) continue;
      // A reverb's room is an asset, resolved like a sampler's zones. Not yet
      // decoded reads as no room, and the synthesised tail plays until the
      // engine has loaded it and applies the project again. It is set on a
      // fresh chain too: building a node gives it its parameters, not its room.
      if (node.setImpulse) {
        node.setImpulse(effect.impulseAssetId ? this.resolveBuffer(effect.impulseAssetId) : null);
      }
      // A fresh chain was built with its parameters and its bypass already.
      if (rebuilt) continue;
      node.setEnabled(effect.enabled);
      for (const [paramId, value] of Object.entries(effect.params)) {
        if (automatedParams.has(`${effect.id}:${paramId}`)) continue;
        node.setParam(paramId, value);
      }
    }
  }

  /**
   * Creates or updates the track's instrument. The node is replaced only when
   * the *type* changes; a parameter tweak must never rebuild it, because that
   * would cut every note currently sounding.
   */
  private updateInstrument(track: Track, automatedParams: ReadonlySet<string>): void {
    const state = track.instrument;
    if (!state) {
      if (this.instrument) {
        this.instrument.dispose();
        this.instrument = null;
        this.instrumentIdentity = null;
      }
      return;
    }

    // A plugin instrument is its URL as much as its type: a different plugin
    // is a different instrument, and the node is rebuilt.
    const identity = state.type === 'wam' ? `wam:${String(state.data.wamUrl ?? '')}` : state.type;
    if (!this.instrument || this.instrumentIdentity !== identity) {
      this.instrument?.dispose();
      this.instrument = createInstrumentNode(this.ctx, state, this.resolveBuffer);
      this.instrumentIdentity = identity;
      this.instrument?.output.connect(this.input);
      const node = this.instrument;
      if (node?.ready) {
        const pending = node.ready.finally(() => this.loading.delete(pending));
        this.loading.add(pending);
      }
      if (node?.wamInfo && this.onWamInfo) {
        void node.wamInfo.then((info) => this.onWamInfo?.(null, info)).catch(() => undefined);
      }
      return;
    }

    for (const [paramId, value] of Object.entries(state.params)) {
      if (automatedParams.has(`instrument:${paramId}`)) continue;
      this.instrument.setParam(paramId, value);
    }
    // Structured state — the sampler's zone map — is pushed on every update, so
    // remapping a zone takes effect without rebuilding the instrument and
    // cutting whatever it is playing.
    this.instrument.setState(state);
  }

  /** The instrument hosted on this track, if it has one. */
  get instrumentNode(): InstrumentNode | null {
    return this.instrument;
  }

  private rebuildEffectChain(effects: Track['effects']): void {
    for (const { node } of this.effects) node.dispose();
    this.effects = [];
    this.input.disconnect();

    let previous: AudioNode = this.input;
    for (const effect of effects) {
      const node = createEffectNode(this.ctx, effect);
      previous.connect(node.input);
      previous = node.output;
      this.effects.push({ id: effect.id, node });
      if (node.ready) {
        const pending = node.ready.finally(() => this.loading.delete(pending));
        this.loading.add(pending);
      }
      if (node.wamInfo && this.onWamInfo) {
        const effectId = effect.id;
        void node.wamInfo.then((info) => this.onWamInfo?.(effectId, info)).catch(() => undefined);
      }
    }
    previous.connect(this.compensation);
    this.effectSignature = effects.map((e) => `${e.id}:${e.type}`).join('|');
  }

  /**
   * Settles once every effect on the chain has finished wiring itself: an
   * insert built in a worklet loads its module first. An offline render waits
   * on this before it schedules automation and starts, or the render would
   * miss both the effect and its lanes.
   */
  whenReady(): Promise<void> | null {
    if (this.loading.size === 0) return null;
    return Promise.all(this.loading).then(() => undefined);
  }

  /** Every hosted plugin on this track: its inserts of that kind, and its instrument when it is one. */
  hostedPlugins(): { effectId: string | null; node: HostedPlugin }[] {
    const out: { effectId: string | null; node: HostedPlugin }[] = [];
    for (const { id, node } of this.effects) if (node.captureState) out.push({ effectId: id, node });
    if (this.instrument?.captureState) out.push({ effectId: null, node: this.instrument });
    return out;
  }

  /** The transport to every hosted plugin on this track. */
  tellTransport(tells: readonly TransportTell[]): void {
    for (const { node } of this.hostedPlugins()) node.tellTransport?.(tells);
  }

  /** How late this track's chain answers: every insert's latency, and the instrument's. */
  chainLatencySec(): number {
    let total = this.instrument?.latencySec?.() ?? 0;
    for (const { node } of this.effects) total += node.latencySec?.() ?? 0;
    return total;
  }

  /**
   * Holds this track's routes back so they line up with the longest: what
   * they all share before the pre-fader tap, what the output still lacks
   * after the pan, and what each send still lacks on that send.
   */
  setCompensation(shared: number, output: number, sends: ReadonlyMap<string, number>): void {
    const set = (param: AudioParam, seconds: number): void => {
      const clamped = Math.min(MAX_COMPENSATION_SEC, Math.max(0, seconds));
      if (Math.abs(param.value - clamped) >= 1e-9) param.value = clamped;
    };
    set(this.compensation.delayTime, shared);
    set(this.outputCompensation.delayTime, output);
    for (const [sendId, node] of this.sendNodes) set(node.delay.delayTime, sends.get(sendId) ?? 0);
  }

  /** The live node for an insert, for a plugin's interface. */
  effectNode(effectId: string): EffectNode | null {
    return this.effects.find((e) => e.id === effectId)?.node ?? null;
  }

  /**
   * The AudioParams an automation lane should write to.
   *
   * `effectId` is null for channel-strip parameters. An unknown or
   * non-rampable parameter yields an empty list, which the engine treats as
   * "nothing to schedule" rather than as an error — a project can legitimately
   * carry a lane for an effect that has since been bypassed or replaced.
   */
  automationTargets(target: AutomationTarget): AutomatableParam[] {
    if (target.kind === 'instrument') return this.instrument?.paramTargets(target.paramId) ?? [];
    if (target.kind === 'effect') {
      return this.effects.find((e) => e.id === target.effectId)?.node.paramTargets(target.paramId) ?? [];
    }
    if (target.paramId === 'volume') return [{ param: this.fader.gain, map: (v) => v }];
    if (target.paramId === 'pan' && this.panner) return [{ param: this.panner.pan, map: (v) => v }];
    return [];
  }

  /**
   * Where a frozen render enters the channel: after the inserts, before the
   * fader. The effects are baked into the file, so running them again would be
   * wrong; the fader, pan, mute and sends stay live, which is what keeps a
   * frozen track mixable.
   */
  get frozenInput(): AudioNode {
    return this.preFaderTap;
  }

  /** Returns the node a send of the given kind should be taken from. */
  sendSource(preFader: boolean): AudioNode {
    return preFader ? this.preFaderTap : this.postFaderTap;
  }

  /**
   * A send's gain and its outlet: tap → gain → compensation delay → outlet.
   * The caller connects the outlet to the target and sets the gain.
   */
  ensureSend(sendId: string, preFader: boolean): { gain: GainNode; out: AudioNode } {
    let node = this.sendNodes.get(sendId);
    if (!node) {
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      const delay = this.ctx.createDelay(MAX_COMPENSATION_SEC);
      delay.delayTime.value = 0;
      gain.connect(delay);
      node = { gain, delay };
      this.sendNodes.set(sendId, node);
    }
    // A send has one source. Disconnecting the node drops what it *feeds*,
    // not what feeds it, so a send switched from post to pre would otherwise
    // keep its old tap as well as gain its new one and carry the track twice.
    for (const tap of [this.preFaderTap, this.postFaderTap]) {
      try {
        tap.disconnect(node.gain);
      } catch {
        /* was not connected */
      }
    }
    this.sendSource(preFader).connect(node.gain);
    return { gain: node.gain, out: node.delay };
  }

  removeSend(sendId: string): void {
    const node = this.sendNodes.get(sendId);
    if (!node) return;
    node.gain.disconnect();
    node.delay.disconnect();
    this.sendNodes.delete(sendId);
  }

  sendIds(): string[] {
    return [...this.sendNodes.keys()];
  }

  connectOutput(destination: AudioNode): void {
    this.output.disconnect();
    this.output.connect(destination);
    this.output.connect(this.splitter);
  }

  disconnectOutput(): void {
    this.output.disconnect();
    this.output.connect(this.splitter);
  }

  /**
   * Reads the meter. Peak is a true sample peak over the analyser window; RMS
   * drives the softer body of the meter. Clipping latches for 1.5 seconds so a
   * single overshoot is visible rather than gone in one frame.
   */
  readMeter(now: number): MeterReading {
    if (this.ctx.state === 'closed') return SILENT_METER;
    this.analyserLeft.getFloatTimeDomainData(this.bufferLeft);
    this.analyserRight.getFloatTimeDomainData(this.bufferRight);

    let peakL = 0;
    let peakR = 0;
    let sumL = 0;
    let sumR = 0;
    for (let i = 0; i < this.bufferLeft.length; i++) {
      const l = Math.abs(this.bufferLeft[i]);
      const r = Math.abs(this.bufferRight[i]);
      if (l > peakL) peakL = l;
      if (r > peakR) peakR = r;
      sumL += this.bufferLeft[i] * this.bufferLeft[i];
      sumR += this.bufferRight[i] * this.bufferRight[i];
    }

    if (peakL > 0.999 || peakR > 0.999) this.clipHoldUntil = now + 1500;

    return {
      peakLeft: peakL,
      peakRight: peakR,
      rmsLeft: Math.sqrt(sumL / this.bufferLeft.length),
      rmsRight: Math.sqrt(sumR / this.bufferRight.length),
      clipped: now < this.clipHoldUntil,
    };
  }

  dispose(): void {
    this.instrument?.dispose();
    this.instrument = null;
    this.instrumentIdentity = null;
    for (const { node } of this.effects) node.dispose();
    this.effects = [];
    for (const node of this.sendNodes.values()) {
      node.gain.disconnect();
      node.delay.disconnect();
    }
    this.sendNodes.clear();
    this.input.disconnect();
    this.preFaderTap.disconnect();
    this.compensation.disconnect();
    this.outputCompensation.disconnect();
    this.postFaderTap.disconnect();
    this.fader.disconnect();
    this.muteGain.disconnect();
    this.panner?.disconnect();
    this.panFallback?.disconnect();
    this.splitter.disconnect();
    this.analyserLeft.disconnect();
    this.analyserRight.disconnect();
    this.output.disconnect();
  }
}
